import React, { useMemo, useState } from "react";
import { CustomDropdown, fieldLabelClasses } from "../../../components/ui";
import { useEnquiries } from "../hooks/useEnquiries";

export interface EnquiryPickerFieldProps {
  /** Prefixes the label's id, so two pickers on one screen stay apart. */
  id: string;
  label: string;
  /** Kept for the reader while taken off the screen — see `ClientPickerField`. */
  hideLabel?: boolean;
  /** The picked enquiry's `public_id`, or "" for none yet. */
  enquiryId: string;
  /** Given the id and the name beside it, so a caller need not hold the list. */
  onChange: (enquiryId: string, name: string) => void;
}

/**
 * How many rows one search asks for.
 *
 * Well past the ten the enquiries table pages at, because this list is read
 * by scrolling rather than by turning pages and there is nothing under it to
 * press for more. Fifty is enough that a practice's whole open pipeline is
 * usually in hand on the first read, and anything past it is reached by
 * typing — which is a server-side search, not a filter over what came back.
 */
const PAGE_SIZE = 50;

/**
 * The stage the booking endpoint will not take, as the list endpoint's
 * `notinclude` spells it — the backend's own status code, the same word the
 * record carries. The screens say "Converted"; see `EnquiryStatus`.
 */
const CONVERTED = "CONVERTED";

/**
 * A searchable dropdown over the practice's enquiries.
 *
 * The enquiry-side twin of `ClientPickerField`, and a second component rather
 * than a flag on that one: the two read different endpoints, and this one's
 * search is the server's. An enquiry list is paged at the API and the search
 * box is a query parameter — see `useEnquiries` — so filtering what one page
 * happened to return would quietly search a tenth of the pipeline and report
 * the rest as "No results".
 *
 * Which is also why `remoteFilter` is on: the rows handed to the dropdown are
 * already the answer to what was typed, and letting it filter them again
 * would drop every match the server made on something other than the name —
 * a phone number, an email — that this list does not draw.
 *
 * Every stage but Converted — `notinclude=CONVERTED`, asked of the server
 * rather than filtered out of what came back.
 *
 * A converted enquiry is a client, and the appointment endpoint refuses to
 * book an hour against one: the hour belongs on the client record it became.
 * So the row is not offered, because offering it is offering a booking that
 * fails — and it is left out at the API rather than here, since this list is
 * paged and dropping rows from a page would quietly make some pages shorter
 * than others and the count above them wrong.
 *
 * Only this picker asks for it. The enquiries table asks for all of them, as
 * it should: the last stage of the pipeline is still part of the pipeline.
 *
 * Its own component so `useEnquiries` is mounted with it: a dialog that never
 * shows this step would otherwise fetch the pipeline to never read it, and
 * toast a failure for a list the flow does not need.
 */
const EnquiryPickerField: React.FC<EnquiryPickerFieldProps> = ({
  id,
  label,
  hideLabel = false,
  enquiryId,
  onChange,
}) => {
  // What the dropdown's own box has been typed into. Handed straight to the
  // hook, which debounces it — so this is one request per pause, not one per
  // keystroke.
  const [search, setSearch] = useState("");
  const { enquiries, status } = useEnquiries({
    search,
    pageSize: PAGE_SIZE,
    notInclude: CONVERTED,
  });

  const options = useMemo(
    () =>
      enquiries.map((enquiry) => ({
        value: enquiry.id,
        label: enquiry.name,
        // The stage under the name: the one thing worth knowing before
        // booking an hour with somebody who has not bought anything, and the
        // one thing that tells two enquiries with the same name apart.
        subtitle: enquiry.statusLabel,
      })),
    [enquiries]
  );

  // The field's own name where the label has been taken off the screen, and
  // "Search enquiry" where it is drawn — the same rule `ClientPickerField`
  // follows, and the note there says why.
  const placeholder =
    status === "failed"
      ? "Could not load your enquiries"
      : hideLabel
      ? label
      : "Search enquiry";

  const labelId = `${id}-label`;

  return (
    <div>
      <label
        id={labelId}
        className={hideLabel ? "sr-only" : `${fieldLabelClasses} mb-1.5`}
      >
        {label}
      </label>
      {/* `CustomDropdown` rather than `DropdownField`: the row has to carry the
          enquiry's id, and the labelled field only deals in strings — two
          enquiries can share a name, an id cannot. */}
      <CustomDropdown
        options={options}
        value={enquiryId}
        onChange={(value) =>
          onChange(
            value,
            options.find((option) => option.value === value)?.label ?? ""
          )
        }
        placeholder={placeholder}
        // Not while a search is in flight. The list is the server's answer and
        // it is replaced wholesale on every pause in typing; a field that went
        // dead for each of those would be a field nobody could type into.
        disabled={status === "failed"}
        searchable
        remoteFilter
        onSearchChange={setSearch}
        loading={status === "loading"}
        // Said in the open list rather than in the placeholder: the field reads
        // "Search enquiry" until it is opened, and opening an empty list is the
        // moment somebody wants to know why there is nothing to search.
        // Named for what is actually being searched: a practice whose every
        // enquiry has converted has enquiries, and an empty list telling it
        // otherwise would read as a list that failed to load.
        emptyMessage="No open enquiries. One that has converted is a client now — book it from their record."
        aria-labelledby={labelId}
      />
    </div>
  );
};

export default EnquiryPickerField;
